import { Grid } from '@mui/material'
import { isLastItem, SortChangeEvent, SortDirection } from '@mprolab/sdk'
import styled from 'styled-components'
import _ from 'lodash'
import React, { useRef, type HTMLAttributes } from 'react'
import { HeightProps, height } from 'styled-system'
import { getHeader, getRows } from './Table.logic'
import { TableColumn, AlignOptions, EmptyRowCell, EmptyTableRow } from './Table.types'
import { Text, TextProps, TextVariant } from '../Text'
import PaginationButtons from './components/PaginationButtons'
import { MediaQuery } from '../../theme/base'
import LoadingRows from './components/LoadingRows'
import { Flex } from '../Flex'

const ContentCard = styled(Flex)`
  flex-direction: column;
  border-radius: 16px;
  padding: 24px 22px;
  width: 100%;
  background: ${({ theme }) => theme.colors.gray10};
`

const StyledTableTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.purple};
  margin-bottom: 24px;
`

const StyledHeaderTitle = styled(Text)<{ align?: AlignOptions }>`
  color: ${({ theme }) => theme.colors.white3};
  display: flex;
  justify-content: ${({ align }) => align || 'flex-start'};
  align-items: center;
`

const DefaultCellText = styled(Text)<{ align?: AlignOptions }>`
  display: flex;
  justify-content: ${({ align }) => align || 'flex-start'};
  align-items: center;
`

const StyledHeaderWrapper = styled(Grid)<{ fullViewMediaquery?: MediaQuery; fullViewCustomWidth: number }>`
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray7};
  padding-bottom: 16px;
  ${({ fullViewMediaquery, fullViewCustomWidth, theme }) =>
    fullViewMediaquery &&
    `
    ${theme.mediaQueries[fullViewMediaquery]} {
      min-width: ${fullViewCustomWidth}px;
    }
`}
`

const StyledRowWrapper = styled.div<
  {
    isLastItem?: boolean
    clickable?: boolean
    fullViewMediaquery?: MediaQuery
    fullViewCustomWidth: number
  } & HTMLAttributes<HTMLDivElement>
>`
  position: relative;
  padding: 8px 0px;
  ${({ isLastItem, theme }) =>
    !isLastItem &&
    `
    border-bottom: 1px solid ${theme.colors.gray7};
  `};

  ${({ clickable }) =>
    clickable &&
    ` 
    cursor: pointer;
  `}
  ${({ fullViewMediaquery, fullViewCustomWidth, theme }) =>
    fullViewMediaquery &&
    `
  ${theme.mediaQueries[fullViewMediaquery]} {
    min-width: ${fullViewCustomWidth}px;
  }
`}
`

const LoadingDataBox = styled.div<HeightProps>`
  @keyframes loading {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0 50%;
    }
  }
  display: flex;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  min-height: 20px;
  height: 100%;
  color: transparent;
  background: ${({ theme }) =>
    `linear-gradient(100deg, ${theme.colors.gray7} 30%, ${theme.colors.gray8} 50%, ${theme.colors.gray7} 70%)`};
  animation: loading 2s ease-in-out infinite;
  background-size: 500%;
  margin: 0px 4px;
  border-radius: 15px;
  ${height}
`

const ListScrollableContainer = styled.div<
  { fullViewMediaquery?: MediaQuery } & HTMLAttributes<HTMLDivElement>
>`
  width: 100%;
  ${({ fullViewMediaquery, theme }) =>
    fullViewMediaquery &&
    `
  ${theme.mediaQueries[fullViewMediaquery]} {
    overflow-x: auto;
    overflow-y: hidden;
  }
`}
`

type CellTextComponent = React.ElementType<TextProps & { align?: AlignOptions }>

const cellComponent = (
  cellValue: string | React.ReactNode,
  align: AlignOptions,
  onSingleCellClick?: (value: string | number | boolean) => void,
  CustomText?: CellTextComponent
) => {
  const handleCellClick = (value: string | number | boolean | undefined) => {
    if (value && onSingleCellClick) onSingleCellClick(value)
  }

  if (typeof cellValue === 'object') return cellValue

  return CustomText ? (
    <CustomText
      align={align}
      onClick={() => {
        handleCellClick(cellValue)
      }}
    >
      {cellValue}
    </CustomText>
  ) : (
    <DefaultCellText
      align={align}
      onClick={() => {
        handleCellClick(cellValue)
      }}
    >
      {cellValue}
    </DefaultCellText>
  )
}

export const onHeaderCellClick = (
  columnKey: string,
  sortDirection?: SortDirection,
  onSortChange?: (e: SortChangeEvent) => void
) => {
  if (onSortChange && sortDirection === SortDirection.ASCENDING) {
    onSortChange({ sortDirection: SortDirection.DESCENDING, key: columnKey })
  } else if (onSortChange && (!sortDirection || sortDirection === SortDirection.DESCENDING)) {
    onSortChange({ sortDirection: SortDirection.ASCENDING, key: columnKey })
  }
}

export interface TableProps {
  tableTitle?: string
  data: any[]
  columns: TableColumn[]
  onRowClick?: (row: any) => void
  onCellClick?: (value: string | number | boolean) => void
  onSortChange?: (e: SortChangeEvent) => void
  rowsPerPage?: number
  dataCount?: number
  onPageChanged?: (pageChangeEvent: { pageNumber: number; pageSize: number; skipNumber: number }) => void
  loading?: boolean
  fetching?: boolean
  error?: boolean
  fullViewMediaquery?: MediaQuery
  fullViewCustomWidth?: number
  emptyListComponent?: React.ReactNode
}

const Table: React.FunctionComponent<TableProps> = (props) => {
  const {
    tableTitle,
    data = [],
    columns,
    onRowClick,
    onCellClick,
    onSortChange,
    rowsPerPage = 5,
    dataCount,
    onPageChanged,
    loading = true,
    fetching = false,
    fullViewMediaquery = MediaQuery.mobile,
    fullViewCustomWidth = 1000,
    emptyListComponent
  } = props

  const tableRows = getRows(data, columns)
  const header = getHeader(columns)
  const tableCellDataRef = useRef<HTMLDivElement>(null)

  const emptyCellsArray: EmptyRowCell[] = columns.map((column: TableColumn) => {
    return { gridColumns: column.gridColumns || 2 }
  })
  const emptyRowsArray: EmptyTableRow[] = Array(rowsPerPage)
    .fill(null)
    .map(() => {
      return { cells: emptyCellsArray }
    })

  const getRowsContent = () => {
    switch (true) {
      case loading:
        return <LoadingRows {...{ emptyRowsArray, fullViewMediaquery, fullViewCustomWidth }} />
      case !_.isEmpty(tableRows):
        return tableRows.map((tableRow, index) => {
          const { cells } = tableRow
          return (
            <StyledRowWrapper
              id={`${tableRow.key}_${index}_tableRow`}
              key={tableRow.key}
              isLastItem={isLastItem(tableRows, index)}
              onClick={() => {
                if (onRowClick && tableRow && tableRow.cells) {
                  let rowObj = {}
                  tableRow.cells.forEach((c) => {
                    rowObj = { ...rowObj, [c.columnKey]: c.value }
                  })
                  onRowClick(rowObj)
                }
              }}
              clickable={!_.isNil(onRowClick)}
              fullViewMediaquery={fullViewMediaquery}
              fullViewCustomWidth={fullViewCustomWidth}
            >
              <Grid container columnSpacing={{ xs: 1 }}>
                {cells.map((cell, index) => {
                  const { gridColumns, value, hide, align } = cell
                  return (
                    <Grid
                      ref={tableCellDataRef}
                      id={`${value}_${index}_tableCell`}
                      item
                      xs={gridColumns || 'auto'}
                      display={hide ? 'none' : 'block'}
                    >
                      {loading || fetching ? (
                        <LoadingDataBox height={tableCellDataRef.current?.clientHeight || '24px'} />
                      ) : (
                        cellComponent(value, align, onCellClick)
                      )}
                    </Grid>
                  )
                })}
              </Grid>
            </StyledRowWrapper>
          )
        })
      case _.isEmpty(tableRows):
        return (
          <LoadingRows {...{ emptyRowsArray, fullViewMediaquery, fullViewCustomWidth }} isEmpty>
            {emptyListComponent}
          </LoadingRows>
        )
      default:
        return <LoadingRows {...{ emptyRowsArray, fullViewMediaquery, fullViewCustomWidth }} />
    }
  }

  return (
    <Flex flexDirection="column">
      <ContentCard>
        {tableTitle && <StyledTableTitle variant={TextVariant.CAPTION}>{tableTitle}</StyledTableTitle>}
        <ListScrollableContainer fullViewMediaquery={fullViewMediaquery}>
          <StyledHeaderWrapper
            container
            fullViewMediaquery={fullViewMediaquery}
            fullViewCustomWidth={fullViewCustomWidth}
            columnSpacing={{ xs: 1 }}
          >
            {!_.isNil(header) ? (
              header?.cells.map((headerCell, index) => {
                const { align, gridColumns, value, columnKey, hide, sortDirection } = headerCell
                return (
                  <Grid
                    id={`${headerCell.value}_${index}_headerCell`}
                    key={columnKey}
                    item
                    xs={gridColumns}
                    onClick={() => onHeaderCellClick(columnKey, sortDirection, onSortChange)}
                    display={hide ? 'none' : 'block'}
                  >
                    {loading ? <LoadingDataBox /> : cellComponent(value, align, onCellClick, StyledHeaderTitle)}
                  </Grid>
                )
              })
            ) : (
              <LoadingRows {...{ emptyRowsArray, fullViewMediaquery, fullViewCustomWidth }} />
            )}
          </StyledHeaderWrapper>
          {getRowsContent()}
        </ListScrollableContainer>
      </ContentCard>
      {rowsPerPage && dataCount && dataCount / rowsPerPage > 1 ? (
        <PaginationButtons
          maxPage={Math.ceil(dataCount / rowsPerPage)}
          onPageChanged={(pageNumber) => {
            if (onPageChanged)
              onPageChanged({ pageNumber, pageSize: rowsPerPage, skipNumber: (pageNumber - 1) * rowsPerPage })
          }}
        />
      ) : (
        <></>
      )}
    </Flex>
  )
}

export default Table
